import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'
import {
  LINEAR_REWARDS_APR_ABI,
  LINEAR_REWARDS_READS,
  linearRewardsApr,
  linearRewardsCalls,
} from '../linearRewards'

/**
 * Frax staked-asset intrinsic yields — `sfrxETH` (LST) and the
 * `sfrxUSD` / `sFRAX` savings vaults.
 *
 * Two *different* reward mechanisms live behind these three tokens, so
 * they get two fetchers rather than one:
 *
 *  - **sfrxETH** is an `xERC4626` over frxETH: validator rewards are
 *    dripped into the vault one week at a time
 *    (`lastRewardAmount` released linearly from `lastSync` to
 *    `rewardsCycleEnd`). Frax publishes the resulting APR directly, so
 *    the API is primary and the on-chain drip is the fallback.
 *  - **sfrxUSD / sFRAX** are `LinearRewardsErc4626` over frxUSD / FRAX.
 *    There is **no APR field on any Frax API endpoint** for these (the
 *    `/v2/sfrxusd/balance-sheet/latest` payload carries reserves and a
 *    share price, not a rate), so they are computed on-chain — which is
 *    exact, see {@link fetchFraxSavingsAprs}.
 *
 * Docs: https://docs.frax.finance/
 */

const CHAIN_ID = Chain.ETHEREUM_MAINNET

/** Frax's public summary endpoint — carries `sfrxethApr` in percent. */
const FRXETH_SUMMARY_URL = 'https://api.frax.finance/v2/frxeth/summary/latest'

/** sfrxETH — `xERC4626` over frxETH. */
const SFRXETH: Address = '0xac3e018457b222d93114458476f3e3416abbe38f'
/** sfrxUSD — `LinearRewardsErc4626` over frxUSD. */
const SFRXUSD: Address = '0xcf62f905562626cfcdd2261162a51fd02fc9c5b6'
/** sFRAX — the pre-rename sibling of sfrxUSD, over legacy FRAX. Still
 *  live and larger than sfrxUSD; same contract family, same maths. */
const SFRAX: Address = '0xa663b02cf0a4b149d2ad41910cb81e23e1c41c32'

const YEAR_SECONDS = 31_536_000n
const ONE_E18 = 10n ** 18n

// Keys. The `*_GROUP_KEY`s are the **verified** `assetGroup` values from the
// 1delta token list for chain 1 — that is what the lending-market join
// (`intrinsicYields[asset.assetGroup]`) looks up, so they are copied
// character-for-character and must not be "tidied". Note the list is not
// self-consistent about casing: sfrxUSD's group spells the symbol lowercase
// (`::sfrxUSD`) while sFRAX's spells it uppercase (`::SFRAX`), and sfrxETH has
// no `Name::SYMBOL` group at all — its group is the bare symbol.
//
// The bare `*_KEY`s are what the LST / savings registries bind `supplyRate`
// through (matching the WSTETH / RETH / PUFETH convention), so both forms are
// emitted; for sfrxETH the two coincide.
const SFRXETH_KEY = 'SFRXETH'
const SFRXUSD_KEY = 'SFRXUSD'
const SFRXUSD_GROUP_KEY = 'Staked Frax USD::sfrxUSD'
const SFRAX_KEY = 'SFRAX'
const SFRAX_GROUP_KEY = 'Staked FRAX::SFRAX'

/** `xERC4626` drip state (sfrxETH). */
const XERC4626_ABI = [
  {
    name: 'rewardsCycleEnd',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint32' }],
  },
  {
    name: 'lastSync',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint32' }],
  },
  {
    name: 'lastRewardAmount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint192' }],
  },
  {
    name: 'totalAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const


// NB: read through `multicallRetryUniversal`, never a bare
// `getEvmClient(chain)` — the latter pins ONE RPC with no failover, and
// several of the mainnet pool's leading endpoints reject `eth_call`
// outright (`api.zan.top` requires registration, `rpc.flashbots.net` does
// not whitelist the method, `rpc.owlracle.info` 401s). A fetcher that
// walks only the first few entries silently reports 0 %. The shared helper
// retries and skips across the whole pool.

const robustFetchJson = async (
  url: string,
  { timeoutMs = 5000, attempts = 3 } = {},
): Promise<any> => {
  let lastErr: unknown
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(url, {
        headers: {
          accept: 'application/json',
          'user-agent': '1delta-margin-fetcher',
        },
        signal: AbortSignal.timeout(timeoutMs),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return await res.json()
    } catch (e) {
      lastErr = e
      if (i < attempts - 1)
        await new Promise((r) => setTimeout(r, 250 * (i + 1)))
    }
  }
  throw lastErr
}

/**
 * sfrxETH APR from the on-chain drip — the fallback when the summary
 * endpoint is unreachable.
 *
 * `xERC4626` releases `lastRewardAmount` linearly across the current
 * cycle, so the instantaneous rate is
 *
 *   apr = lastRewardAmount / (rewardsCycleEnd - lastSync) * yearSeconds
 *         / totalAssets
 *
 * Two caveats make this the *fallback* rather than the primary source:
 * the figure is a one-week snapshot (noisier than Frax's smoothed
 * publication), and once a cycle ends without a `syncRewards()` poke
 * the stale `lastRewardAmount` keeps being reported.
 */
const fetchSfrxEthAprOnChain = async (): Promise<number> => {
  const [cycleEnd, lastSync, lastRewardAmount, totalAssets] =
    (await multicallRetryUniversal({
      chain: CHAIN_ID,
      abi: XERC4626_ABI,
      calls: [
        { address: SFRXETH, name: 'rewardsCycleEnd', params: [] },
        { address: SFRXETH, name: 'lastSync', params: [] },
        { address: SFRXETH, name: 'lastRewardAmount', params: [] },
        { address: SFRXETH, name: 'totalAssets', params: [] },
      ],
      allowFailure: false,
    })) as [number, number, bigint, bigint]

  const window = BigInt(cycleEnd) - BigInt(lastSync)
  if (window <= 0n || totalAssets === 0n) return 0
  const perSecond = lastRewardAmount / window
  return Number((perSecond * YEAR_SECONDS * ONE_E18) / totalAssets) / 1e16
}

/**
 * sfrxETH APR — Frax's published figure first, on-chain drip second.
 * `sfrxethApr` is already a percent (e.g. `2.7179` = 2.72 %).
 */
const fetchSfrxEthApr = async (): Promise<number> => {
  try {
    const res = await robustFetchJson(FRXETH_SUMMARY_URL)
    const apr = Number(res?.sfrxethApr)
    if (Number.isFinite(apr) && apr >= 0) return apr
    throw new Error('sfrxethApr missing or non-numeric')
  } catch {
    return fetchSfrxEthAprOnChain()
  }
}

/**
 * sfrxUSD + sFRAX APRs, computed on-chain in one multicall.
 *
 * `LinearRewardsErc4626.calculateRewardsToDistribute` pays out
 *
 *   min( rewardCycleAmount / (cycleEnd - lastSync),
 *        maxDistributionPerSecondPerAsset * storedTotalAssets / 1e18 )
 *
 * per second, so the APR is that rate annualised over
 * `storedTotalAssets`. **The cap is the binding term in practice** —
 * Frax deliberately over-funds each cycle and lets
 * `maxDistributionPerSecondPerAsset` set the advertised rate, which is
 * why the uncapped leg reads absurdly high on both vaults. Both terms
 * are evaluated anyway so the number stays right if that ever changes.
 *
 * Verified 2026-07-30: sfrxUSD → 4.0182 % APR (≙ 4.100 % APY, matching
 * DefiLlama's 4.09942 %); sFRAX → 1.2423 % APR.
 */
const fetchFraxSavingsAprs = async (): Promise<{
  sfrxUsd: number
  sfrax: number
}> => {
  const results = (await multicallRetryUniversal({
    chain: CHAIN_ID,
    abi: LINEAR_REWARDS_APR_ABI as any,
    calls: [SFRXUSD, SFRAX].flatMap((address) => linearRewardsCalls(address)),
    allowFailure: false,
  })) as unknown[]

  // The maths is shared with every other `LinearRewardsErc4626` vault we read
  // (Resupply's sreUSD is the same contract family) — see `linearRewards.ts`.
  return {
    sfrxUsd: linearRewardsApr(results, 0),
    sfrax: linearRewardsApr(results, LINEAR_REWARDS_READS),
  }
}

/** sfrxETH — Frax's ETH liquid-staking vault (`xERC4626` over frxETH). */
export const sfrxEthFetcher: YieldFetcher = {
  label: 'SFRXETH',
  fetch: async () => {
    const apr = await fetchSfrxEthApr()
    // sfrxETH's token-list `assetGroup` is the bare symbol, so one key
    // serves both the registry binding and the lending join.
    return { [SFRXETH_KEY]: apr }
  },
}

/**
 * sfrxUSD + sFRAX — Frax's stablecoin savings vaults. One fetcher for
 * both: same contract family, one multicall, and the savings registry
 * points both entries here via distinct `yieldKey`s.
 */
export const fraxSavingsFetcher: YieldFetcher = {
  label: 'SFRXUSD_SFRAX',
  fetch: async () => {
    const { sfrxUsd, sfrax } = await fetchFraxSavingsAprs()
    return {
      [SFRXUSD_KEY]: sfrxUsd,
      [SFRXUSD_GROUP_KEY]: sfrxUsd,
      [SFRAX_KEY]: sfrax,
      [SFRAX_GROUP_KEY]: sfrax,
    }
  },
}
